import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const projects = [
  {
    title: 'NA4WX Allstar Cloud',
    text: 'A cloud-based remote management platform for AllStarLink node operators, letting you monitor and control your own repeater or simplex node — linking/unlinking, live status, sounds, and full configuration — from any browser, anywhere with an internet connection, with no VPN or port forwarding required.',
    href: 'https://allstar.na4wx.com',
  },
    {
    title: 'NA4WX Allstar Dashboard (ASL3)',
    text: "A locally-hosted configuration and control dashboard for AllStarLink node operators, running directly on the node's own hardware (e.g. a HamVoIP Raspberry Pi). It gives full browser-based control over the node — radio hardware, timing, identity, DTMF commands, sounds, weather alerts, and Asterisk service management — without SSH or manually editing config files. Works entirely over the local network on its own; remote access from anywhere is a separate, optional add-on (NA4WX Allstar Cloud).",
    href: 'https://github.com/na4wx/na4wx_allstar_asl3_dashboard',
  },
  {
    title: 'NA4WX Allstar Dashboard (HamVOIP)',
    text: "A locally-hosted configuration and control dashboard for AllStarLink node operators, running directly on the node's own hardware (e.g. a HamVoIP Raspberry Pi). It gives full browser-based control over the node — radio hardware, timing, identity, DTMF commands, sounds, weather alerts, and Asterisk service management — without SSH or manually editing config files. Works entirely over the local network on its own; remote access from anywhere is a separate, optional add-on (NA4WX Allstar Cloud).",
    href: 'https://github.com/na4wx/na4wx_allstar_dashboard',
  },
  {
    title: 'PTTalk',
    text: 'PTTalk is a push-to-talk communication platform built for teams and organizations, featuring a browser-based dispatch console alongside mobile and desktop clients for real-time voice coordination.',
    href: 'https://pttalk.na4wx.com',
  },
  {
    title: 'WB4GBI',
    text: 'The website wb4gbi.com is dedicated to the WB4GBI Repeater System, which serves East Tennessee. This repeater system is managed by Tim Berry, WB4GBI. The site offers a wealth of information about the various repeaters under the WB4GBI call sign, including technical specifications, locations, and operational guidelines.',
    href: 'https://wb4gbi.com',
  },
  {
    title: 'ETSKYWARN',
    text: 'The website etskywarn.org is dedicated to East Tennessee Skywarn, a volunteer program that focuses on weather spotting and reporting for the National Weather Service (NWS) in Morristown, TN. The program involves trained amateur radio operators who provide real-time reports during severe weather events.',
    href: 'https://etskywarn.com',
  },
];

// Approximate GitHub language colors, with a fallback cycle for anything not listed.
const LANGUAGE_COLORS = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  Python: '#3572A5',
  Go: '#00ADD8',
  Java: '#b07219',
  'C++': '#f34b7d',
  C: '#555555',
  'C#': '#178600',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Shell: '#89e051',
  PHP: '#4F5D95',
  Ruby: '#701516',
  'VB.NET': '#945db7',
  VBA: '#945db7',
  Dockerfile: '#384d54',
};
const FALLBACK_COLORS = ['#0d6efd', '#00C49F', '#FFBB28', '#FF8042', '#845EC2', '#D65DB1', '#FF6F91'];
const colorForLanguage = (name, index) => LANGUAGE_COLORS[name] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

const timeAgo = (dateString) => {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  const intervals = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, secs] of intervals) {
    const count = Math.floor(seconds / secs);
    if (count >= 1) return `${count} ${unit}${count > 1 ? 's' : ''} ago`;
  }
  return 'just now';
};

const Development = () => {
  const [githubStats, setGithubStats] = useState(null);

  useEffect(() => {
    fetch('/githubStats.json')
      .then(res => res.json())
      .then(setGithubStats)
      .catch(error => console.error('There was an error loading GitHub stats!', error));
  }, []);

  const reposData = githubStats?.reposData || [];

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        Software Development
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
        Jordan is proficient in various programming languages and technologies, including React, Node.js,
        JavaScript, Python, VB.NET, Java, and Go.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom>
        Notable Projects
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5, justifyContent: 'center' }}>
        {projects.map((project) => (
          <Grid size={{ xs: 12, md: 4 }} key={project.title}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {project.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.text}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  href={project.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                >
                  View Website
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" component="h2" gutterBottom>
        Repositories
      </Typography>
      {!githubStats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 4, p: { xs: 2, sm: 3 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center' }}>
              <Avatar src={githubStats.profile.avatar_url} sx={{ width: 88, height: 88 }} />
              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="h6">
                  {githubStats.profile.name || githubStats.profile.login}
                </Typography>
                {githubStats.profile.bio && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {githubStats.profile.bio}
                  </Typography>
                )}
                <Stack direction="row" spacing={3} sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <Typography variant="body2">
                    <strong>{githubStats.profile.public_repos}</strong> repos
                  </Typography>
                  <Typography variant="body2">
                    <strong>{githubStats.profile.followers}</strong> followers
                  </Typography>
                </Stack>
              </Box>
              <Button
                variant="outlined"
                href={githubStats.profile.html_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View GitHub Profile
              </Button>
            </Stack>
          </Card>

          <Card sx={{ mb: 4, p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle1" gutterBottom>
              Languages
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={githubStats.languagesData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={2}
                >
                  {githubStats.languagesData.map((entry, index) => (
                    <Cell key={entry.name} fill={colorForLanguage(entry.name, index)} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Grid container spacing={3}>
            {reposData.map((repo, index) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={repo.name}>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="h3" sx={{ wordBreak: 'break-word' }}>
                          {repo.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0, ml: 1 }}>
                          <StarRoundedIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          <Typography variant="body2">{repo.stargazers_count}</Typography>
                        </Stack>
                      </Stack>
                      {repo.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {repo.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        {repo.language && (
                          <Chip
                            size="small"
                            label={repo.language}
                            sx={{ bgcolor: colorForLanguage(repo.language, index), color: '#fff' }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Updated {timeAgo(repo.updated_at)}
                        </Typography>
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        href={repo.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Repo
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Development;
